import { useEffect } from "react";
import { Checkbox } from "@leafygreen-ui/checkbox";
import { cx } from "../../utils/css";
import { FilterInputControls } from "./FilterInputControls";
import styles from "./TreeSelect.module.css";

export const ALL_VALUE = "all";
const ALL_COPY = "All";
export interface TreeSelectProps {
  "data-testid"?: string;
  isVisible?: boolean;
  onChange: (s: string[]) => void;
  setOptionsLabel?: (v: string) => void;
  state: string[];
  tData: TreeDataEntry[];
  onReset?: () => void;
  onFilter?: () => void;
}
export interface TreeDataChildEntry {
  title: string;
  value: string;
  key: string;
}
export interface TreeDataEntry extends TreeDataChildEntry {
  children?: TreeDataChildEntry[];
}

export const TreeSelect: React.FC<TreeSelectProps> = ({
  "data-testid": dataTestId,
  isVisible = true,
  onChange,
  onFilter,
  onReset,
  setOptionsLabel = () => undefined,
  state,
  tData,
}) => {
  const allValues = getAllValues(tData);
  // removes values not included in tData
  const filteredState = state.filter((value) => allValues.includes(value));
  const optionsLabel = filteredState.includes(ALL_VALUE)
    ? ALL_COPY
    : filteredState
        .reduce(
          // remove children nodes if parent exists in state
          (accum, value) => {
            const { target } = findNode({ value, tData });
            if (target?.children) {
              const { children } = target;
              return accum.filter(
                (v) => !children.find((child) => child.value === v),
              );
            }
            return accum;
          },
          [...filteredState],
        )
        .map((value) => findNode({ value, tData }).target?.title ?? value)
        .join(", ");

  useEffect(() => {
    setOptionsLabel(optionsLabel);
  }, [optionsLabel, setOptionsLabel]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={styles.checkboxContainer}
      data-testid={dataTestId || "tree-select-options"}
    >
      {renderCheckboxes({
        state: filteredState,
        tData,
        onChange,
      })}
      {onReset && onFilter && (
        <FilterInputControls
          onClickReset={onReset}
          onClickSubmit={onFilter}
          submitButtonCopy="Filter"
        />
      )}
    </div>
  );
};

// depth first traversal checkbox data.
// pushes parent then children to rows array
// keeps track of level for indentation
const renderCheckboxes = ({
  onChange,
  state,
  tData,
}: {
  tData: TreeDataEntry[];
  state: string[];
  onChange: (v: string[]) => void;
}): React.JSX.Element[] => {
  const rows: React.JSX.Element[] = [];
  tData.forEach((entry) => {
    renderCheckboxesHelper({ rows, data: entry, onChange, state, tData });
  });
  return rows;
};

const renderCheckboxesHelper = ({
  data,
  onChange,
  rows,
  state,
  tData,
}: {
  rows: React.JSX.Element[];
  data: TreeDataEntry;
  onChange: (v: string[]) => void;
  state: string[];
  tData: TreeDataEntry[];
}): void => {
  // push parent
  const onChangeFn = (): void =>
    handleOnChange({ state, value: data.value, onChange, tData });
  rows.push(
    <div
      key={data.key}
      className={cx(
        styles.checkboxWrapper,
        data.value === ALL_VALUE && styles.checkboxWrapperAll,
      )}
    >
      <Checkbox
        bold={false}
        checked={state.includes(data.value)}
        label={data.title}
        onChange={onChangeFn}
      />
    </div>,
  );
  // then examine children
  if (data.children) {
    data.children.forEach((child) => {
      const onChangeChildFn = (): void =>
        handleOnChange({ state, value: child.value, onChange, tData });
      rows.push(
        <div
          key={`${data.key}-${child.key}`}
          className={cx(
            styles.checkboxWrapper,
            styles.checkboxWrapperNested,
            child.value === ALL_VALUE && styles.checkboxWrapperAll,
          )}
        >
          <Checkbox
            bold={false}
            checked={state.includes(child.value)}
            label={child.title}
            onChange={onChangeChildFn}
          />
        </div>,
      );
    });
  }
};

// Executes when checkbox is clicked
const handleOnChange = ({
  onChange,
  state,
  tData, // callback function
  value,
}: {
  state: string[];
  value: string;
  onChange: (v: string[]) => void;
  tData: TreeDataEntry[];
}): void => {
  const isAlreadyChecked = state.includes(value); // is checkbox already selected
  const { parent, siblings, target } = findNode({ value, tData });
  if (!target) {
    onChange([...state]);
    return;
  }
  // is all button checked
  const isAll = target.value === ALL_VALUE;
  if (isAll) {
    if (isAlreadyChecked) {
      onChange([]);
    } else {
      onChange(getAllValues(tData));
    }
  } else if (target.children) {
    // has list of children
    const childrenValues = target.children.map((child) => child.value);
    if (isAlreadyChecked) {
      onChange(
        adjustAll({
          resultState: state.filter(
            (v) => v !== value && !childrenValues.includes(v),
          ),
          tData,
        }),
      );
    } else {
      const resultState = Array.from(
        new Set([...state, value, ...childrenValues]),
      );
      onChange(adjustAll({ resultState, tData }));
    }
  } else {
    // does not have list of children, could be child
    const parentValue = parent ? parent.value : "";
    if (isAlreadyChecked) {
      onChange(
        adjustAll({
          resultState: state.filter((v) => v !== value && v !== parentValue),
          tData,
        }),
      );
    } else {
      let siblingsChecked = true;
      siblings.forEach((sibling) => {
        siblingsChecked = siblingsChecked && state.includes(sibling.value);
      });
      const shouldCheckParent = parentValue && siblingsChecked;
      // use set in case parent.value already exists in state
      const resultState = Array.from(
        new Set(
          [...state, value].concat(shouldCheckParent ? [parentValue] : []),
        ),
      );
      onChange(adjustAll({ resultState, tData }));
    }
  }
};

// selects or deselects the All checkbox depending on current options
const adjustAll = ({
  resultState,
  tData,
}: {
  resultState: string[];
  tData: TreeDataEntry[];
}): string[] => {
  const allValues = getAllValues(tData).filter((value) => value !== ALL_VALUE);
  const resultStateHasAllValues = allValues.reduce(
    (accum, value) => accum && resultState.includes(value),
    true,
  );
  // convert to set in case all exists in URL when its not supposed to
  const resultStateSet = new Set(resultState);
  if (resultStateHasAllValues) {
    resultStateSet.add(ALL_VALUE);
  } else {
    resultStateSet.delete(ALL_VALUE);
  }
  return Array.from(resultStateSet);
};

interface FindNodeResult {
  target: TreeDataEntry | null;
  parent: TreeDataEntry | null;
  siblings: TreeDataEntry[] | TreeDataChildEntry[];
}

const findNode = ({
  tData,
  value,
}: {
  value: string;
  tData: TreeDataEntry[];
}): FindNodeResult => {
  for (const curr of tData) {
    if (curr.value === value) {
      return {
        target: curr,
        parent: null,
        siblings: tData.filter((v) => v.value !== value),
      };
    }
    if (curr.children) {
      const child = curr.children.find((c) => c.value === value);
      if (child) {
        return {
          target: child,
          parent: curr,
          siblings: curr.children.filter((c) => c.value !== value),
        };
      }
    }
  }
  return {
    target: null,
    parent: null,
    siblings: [],
  };
};

// returns all values in tData from parents and children
const getAllValues = (tData: TreeDataEntry[]): string[] =>
  tData.reduce<string[]>((accum, currNode) => {
    const childrenValues = currNode.children
      ? currNode.children.map((child) => child.value)
      : [];
    return accum.concat([currNode.value]).concat(childrenValues);
  }, []);
